import { useState, useContext } from "react";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { ConfigurationContext } from "../Context";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/24/solid";
import { usePost } from "../API";
import { useLocalizedStrings } from "../Localization";

interface TagSelectorProps {
  onChange: (tags: string[]) => void;
  selectedTags?: string[];
  className?: string;
  placeholder?: string;
}

const DEFAULT_TAGS = ['Additional Resources', 'Application Issue', 'Eligibility', 'Liveness Issue', 'Payment', 'Recollect'];

export function TagSelector({ selectedTags = [], onChange, className = '', placeholder }: TagSelectorProps) {
  const [query, setQuery] = useState('');
  const getProgramTags = usePost("/support/get_program_tags");
  const L = useLocalizedStrings();
  const config = useContext(ConfigurationContext);

  // State to hold all tags used in the program except those already selected
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  useAsyncEffect(async () => {
    const result = await getProgramTags({});
    // Merge program and default tags, remove duplicates, exclude selected, and sort alphabetically
    const mergedTags = Array.from(
      new Set([
        ...(Array.isArray(result?.tags)
          ? result.tags.filter((tag): tag is string => typeof tag === 'string' && tag != null)
          : []),
        ...DEFAULT_TAGS,
      ])
    )
      .filter((tag) => !selectedTags.includes(tag))
      .sort((a, b) => a.localeCompare(b));
    setAvailableTags(mergedTags);
  }, [selectedTags]);

  const lowerQuery = query.toLowerCase();
  
  // Look for a tag matching the query
  const selectedMatch = selectedTags.find(t => t.toLowerCase() === lowerQuery);
  
  // Check if a tag matching the query exists in the remaining available tags
  const availableMatch = availableTags.find(t => t.toLowerCase() === lowerQuery);

  // Get a filtered list of partial matches
  const filteredTags = query === ''
    ? availableTags
    : availableTags.filter(t => t.toLowerCase().includes(lowerQuery));

  const userCanCreateNewTags = config?.roles?.includes('admin');
  const hasExactAvailableMatch = availableTags.some(tag => tag.toLowerCase() === query.toLowerCase());

  // Build the list of options to display in the dropdown
  let options: string[];
  if (selectedMatch) {
    // Only show the selected tag if it matches the query (with message 'Already added')
    options = [selectedMatch];
  } else if (query === '' || hasExactAvailableMatch || !userCanCreateNewTags) {
    // Show filtered tags if query is empty, matches an available tag, or user can't create new tags
    options = filteredTags;
  } else {
    // Admins: allow adding a new tag if it doesn't match any existing tag
    options = [...filteredTags, query];
  }

  const isNew = query && !selectedMatch && !availableMatch;

  return (
    <div className={`relative ${className}`}>
      {/* Using headlessui Combobox here instead of component-library
      version because 'add new tag' functionality is needed */}
      <Combobox
        value={selectedTags}
        onChange={(newTags: string[]) => {
          const newTag = newTags[newTags.length - 1];
          if (!newTag) return;
          // Only add if not already selected
          if (!selectedTags.includes(newTag)) {
            onChange([...selectedTags, newTag]);
          }
          setQuery('');
        }}
        multiple
      >
        <div className="relative my-2">
          <Combobox.Input
            aria-label={placeholder || L.support.tags.search_tags}
            className="block rounded-md w-full bg-white py-1.5 pl-3 pr-12
              text-sm text-gray-900 border placeholder:text-gray-400 sm:text-sm/6
              focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
            placeholder={placeholder || L.support.tags.search_tags}
            onChange={(e) => setQuery(e.target.value)}
            value={query}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none" aria-label={L.support.tags.toggle_tag_list}>
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Transition
          enterFrom="transform opacity-0 scale-95 z-0"
          enterTo="transform opacity-100 scale-100 z-50"
          leave="transition ease-in duration-75 z-50"
          leaveFrom="transform opacity-100 scale-100 z-50"
          leaveTo="transform opacity-0 scale-95 z-0"
        >
          <Combobox.Options
            className="absolute right-0 z-10 my-2 w-full max-h-80 overflow-y-auto empty:invisible rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
          >
            {/* Show 'no tags found' for non-admins when no matches */}
            {filteredTags.length === 0 && !userCanCreateNewTags && query !== '' && (
              <div className="px-4 py-2 text-sm text-gray-500 cursor-default select-none">
                {L.support.tags.no_tags_found}
              </div>
            )}
            {options.map((tag) => {
              const isDisabled = !!selectedMatch && tag === selectedMatch;
              const isNewOption = tag === query && isNew;

              return (
                <Combobox.Option
                  key={tag}
                  value={tag}
                  disabled={isDisabled}
                  className={({ active }) =>
                    `relative select-none py-2 px-4 text-sm font-normal ${
                      isDisabled 
                        ? 'bg-gray-50 text-gray-500 cursor-default' 
                        : active 
                          ? 'bg-indigo-600 text-white cursor-pointer' 
                          : 'text-gray-900 cursor-pointer'
                    }`
                  }
                >
                  {({ active }) => (
                    <span className="block truncate">
                      {isNewOption && userCanCreateNewTags ? (
                        <>
                          {/* Show 'Add new tag' for new tag option */}
                          <span className="mr-1.5">{L.support.tags.add_new_tag}</span>
                          <span className="font-semibold">{tag}</span>
                        </>
                      ) : selectedMatch ? (
                        <>
                          {/* Indicate tag is already added */}
                          <span className="mr-1.5">{L.support.tags.already_added}</span>
                          <span className="font-semibold">{tag}</span>
                        </>
                      ) : (
                        tag
                      )}
                    </span>
                  )}
                </Combobox.Option>
              );
            })}
          </Combobox.Options>
        </Transition>
      </Combobox>
    </div>
  );
} 